import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import resetPasswordImage from '../../assets/images/services-2.png';
import tecnvironsLogo from '../../assets/images/Tecnvirons-logo.jpeg';
import './ResetPasswordModal.css';

const ResetPasswordModal = ({ isOpen, onClose, onPasswordReset, onBackToLogin, userEmail, userPhone, userIdentifierType = 'email', userType = 'user', clientId = null, resetToken = '' }) => {
  const {
    resetPassword,
    adminResetPassword,
    rbacResetPassword,
    adminRbacResetPassword
  } = useAuth();
  const [formData, setFormData] = useState({
    otp: resetToken || '',
    password: '',
    confirmPassword: ''
  });
  const [authType, setAuthType] = useState(userIdentifierType || 'email');

  React.useEffect(() => {
    if (isOpen) {
      setFormData({
        otp: resetToken || '',
        password: '',
        confirmPassword: ''
      });
      setErrors({});
      setSuccess('');
      if (userIdentifierType) {
        setAuthType(userIdentifierType);
      }
    }
  }, [isOpen, userEmail, userPhone, userIdentifierType, resetToken]);

  React.useEffect(() => {
    if (!isOpen) return undefined;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.otp) {
      newErrors.otp = 'Reset code is required';
    } else if (!/^\d{6}$/.test(formData.otp)) {
      newErrors.otp = 'Reset code must be 6 digits';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    const identifier = authType === 'email' ? userEmail : userPhone;
    if (!identifier) {
      newErrors.general = authType === 'email'
        ? 'Email address is missing'
        : 'Phone number is missing';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const identifier = authType === 'email' ? userEmail : userPhone;
    if (!identifier) {
      setErrors({
        general: `${authType === 'email' ? 'Email address' : 'Phone number'} is missing. Please go back and try the forgot password process again.`
      });
      return;
    }

    setIsLoading(true);

    try {
      let result;
      if (userType === 'admin') {
        result = await adminResetPassword(identifier, formData.otp, formData.password, authType);
      } else if (userType === 'rbac') {
        result = await rbacResetPassword({
          email: identifier,
          client_id: clientId,
          otp: formData.otp,
          password: formData.password
        });
      } else if (userType === 'admin-rbac') {
        result = await adminRbacResetPassword({
          email: identifier,
          admin_id: clientId,
          otp: formData.otp,
          password: formData.password
        });
      } else {
        result = await resetPassword(identifier, formData.otp, formData.password, authType);
      }

      if (result.success) {
        setSuccess('Password reset successfully!');
        setFormData({ otp: '', password: '', confirmPassword: '' });

        setTimeout(() => {
          if (onPasswordReset) {
            onPasswordReset();
          } else {
            onBackToLogin && onBackToLogin();
          }
        }, 2000);
      } else {
        setErrors({ general: result.error });
      }
    } catch (error) {
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setFormData({ otp: '', password: '', confirmPassword: '' });
    setErrors({});
    setSuccess('');
    onBackToLogin && onBackToLogin();
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="reset-password-modal-overlay" onClick={onClose}>
      <div
        className="reset-password-modal-container auth-modal"
        onClick={e => e.stopPropagation()}
        style={{ '--modal-bg-image': `url(${resetPasswordImage})` }}
      >
        <button className="modal-close-btn" onClick={onClose} aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
        </button>

        <div className="modal-form-section">
          <div className="form-content" data-lenis-prevent>
            <div className="modal-header">
              <div className="modal-logo">
                <img src={tecnvironsLogo} alt="Tecnvirons" className="modal-logo-image" />
              </div>
            </div>

            <div className="form-main">
              <h2 className="form-title">Reset Your Password</h2>
              <p className="form-subtitle">
                {authType === 'email'
                  ? `Enter the code we sent to ${userEmail || 'your email'} and your new password.`
                  : `Enter the code we sent to ${userPhone || 'your phone'} and your new password.`}
              </p>

              {success && <div className="success-message">{success}</div>}
              {errors.general && <div className="error-message">{errors.general}</div>}

              <form onSubmit={handleSubmit} className="reset-password-form">
                <div className="modal-form-group">
                  <input
                    type="text"
                    id="otp"
                    name="otp"
                    placeholder=" "
                    value={formData.otp}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    maxLength="6"
                    pattern="[0-9]*"
                    inputMode="numeric"
                    required
                    autoFocus
                  />
                  <label htmlFor="otp">Reset Code</label>
                  {errors.otp && <span className="field-error">{errors.otp}</span>}
                </div>

                <div className="modal-form-group">
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      placeholder=" "
                      value={formData.password}
                      onChange={handleInputChange}
                      disabled={isLoading}
                      required
                    />
                    <label htmlFor="password">New Password</label>
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isLoading}
                    >
                      {showPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                          <line x1="1" y1="1" x2="23" y2="23"></line>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.password && <span className="field-error">{errors.password}</span>}
                </div>

                <div className="modal-form-group">
                  <div className="password-input-wrapper">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      name="confirmPassword"
                      placeholder=" "
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      disabled={isLoading}
                      required
                    />
                    <label htmlFor="confirmPassword">Confirm Password</label>
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      disabled={isLoading}
                    >
                      {showConfirmPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                          <line x1="1" y1="1" x2="23" y2="23"></line>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && <span className="field-error">{errors.confirmPassword}</span>}
                </div>

                <div className="password-requirements">
                  <h4>Password Requirements:</h4>
                  <ul className="requirements-list">
                    <li className={formData.password.length >= 8 ? 'met' : 'unmet'}>
                      {formData.password.length >= 8 ? <CheckCircle size={14} /> : <XCircle size={14} />}
                      <span>At least 8 characters long</span>
                    </li>
                    <li className={/[A-Z]/.test(formData.password) ? 'met' : 'unmet'}>
                      {/[A-Z]/.test(formData.password) ? <CheckCircle size={14} /> : <XCircle size={14} />}
                      <span>One uppercase letter</span>
                    </li>
                    <li className={/[a-z]/.test(formData.password) ? 'met' : 'unmet'}>
                      {/[a-z]/.test(formData.password) ? <CheckCircle size={14} /> : <XCircle size={14} />}
                      <span>One lowercase letter</span>
                    </li>
                    <li className={/\d/.test(formData.password) ? 'met' : 'unmet'}>
                      {/\d/.test(formData.password) ? <CheckCircle size={14} /> : <XCircle size={14} />}
                      <span>One number</span>
                    </li>
                  </ul>
                </div>

                <button
                  type="submit"
                  className="submit-btn"
                  disabled={isLoading || !formData.otp || !formData.password || !formData.confirmPassword}
                >
                  {isLoading ? 'Resetting Password...' : 'Reset Password'}
                </button>
              </form>

              <div className="form-footer">
                <p>
                  Remember your password?{' '}
                  <button type="button" className="link-btn" onClick={handleBackToLogin}>
                    Back to Login
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-image-section">
          <img src={resetPasswordImage} alt="Reset Password" className="modal-image" />
          <div className="image-overlay">
            <div className="decorative-shape top-left-shape"></div>
            <div className="decorative-shape bottom-right-shape"></div>
            <div className="overlay-content">
              <div className="overlay-card">
                <h3>Reset Made Easy!</h3>
                <p>We'll help you get back into your account quickly and securely.</p>
              </div>
              <div className="bottom-overlay">
                <h3>Secure Password Recovery,<br />Your Way!</h3>
                <p>Account security is our priority.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ResetPasswordModal;
